import React from 'react';
import {
  CheckCircle,
  XCircle,
  Download,
  ExternalLink,
} from 'lucide-react';

import { surveyApi } from '../services/api';
import { CATEGORY_DISPLAY_NAMES, Category } from '../types/instrument';
import { SurveyAnalysisResult } from '../types/survey';

interface ResultsDashboardProps {
  result: SurveyAnalysisResult;
  onExport?: () => void;
}

// Mirrors the score-band coloring in surveyshield/templates/_macros.html so
// the React dashboard and the server-rendered HTML report agree on red/yellow/green.
const bandClass = (score: number): string => {
  if (score >= 70) return 'text-green-600 bg-green-50 border-green-200';
  if (score >= 40) return 'text-yellow-600 bg-yellow-50 border-yellow-200';
  return 'text-red-600 bg-red-50 border-red-200';
};

const invertedBandClass = (score: number): string => {
  if (score >= 70) return 'text-red-600 bg-red-50 border-red-200';
  if (score >= 40) return 'text-yellow-600 bg-yellow-50 border-yellow-200';
  return 'text-green-600 bg-green-50 border-green-200';
};

const ResultsDashboard: React.FC<ResultsDashboardProps> = ({ result, onExport }) => {
  const totalFindings = result.categories.reduce(
    (sum, c) => sum + (c.error ? 0 : c.findings.length),
    0,
  );
  const failedCategoryCount = result.categories.filter((c) => c.error).length;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-sm border p-6">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 mb-1">Live runtime review</h2>
            <p className="text-sm text-gray-600">
              {result.survey_domain} · {new Date(result.timestamp).toLocaleString()}
            </p>
          </div>
          <div className="flex items-center space-x-2">
            <a
              href={surveyApi.reportUrl(result.analysis_id)}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
            >
              <ExternalLink className="w-4 h-4 mr-2" />
              Open Full Report
            </a>
            {onExport && (
              <button
                onClick={onExport}
                className="flex items-center px-4 py-2 border border-primary-600 text-primary-700 rounded-lg hover:bg-primary-50 transition-colors"
              >
                <Download className="w-4 h-4 mr-2" />
                Export JSON
              </button>
            )}
          </div>
        </div>

        {/* Outcome banner */}
        <div
          className={`p-4 rounded-lg flex items-start space-x-3 ${
            result.completed
              ? 'bg-green-50 border border-green-200'
              : 'bg-red-50 border border-red-200'
          }`}
        >
          {result.completed ? (
            <CheckCircle className="w-5 h-5 text-green-700 mt-0.5 flex-shrink-0" />
          ) : (
            <XCircle className="w-5 h-5 text-red-700 mt-0.5 flex-shrink-0" />
          )}
          <div className="flex-1 text-sm">
            <p
              className={`font-medium ${
                result.completed ? 'text-green-900' : 'text-red-900'
              }`}
            >
              {result.completed
                ? 'The agent completed the survey end-to-end.'
                : 'The agent did not complete the survey.'}
            </p>
            {result.overall_feedback?.headline && (
              <p
                className={`mt-1 ${
                  result.completed ? 'text-green-800' : 'text-red-800'
                }`}
              >
                {result.overall_feedback.headline}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Score panel */}
      <div className="bg-white rounded-lg shadow-sm border p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Bot resistance</h3>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <ScoreTile
            label="Defense score"
            value={result.defense_score}
            band={bandClass(result.defense_score)}
            suffix="/100"
          />
          <ScoreTile
            label="Bot completion likelihood"
            value={result.bot_completion_likelihood}
            band={invertedBandClass(result.bot_completion_likelihood)}
            suffix="/100"
            note="lower is better"
          />
          <ScoreTile
            label="Categories reviewed"
            value={result.categories.length}
            band="text-gray-900 bg-gray-50 border-gray-200"
            raw
          />
          <ScoreTile
            label="Findings"
            value={totalFindings}
            band="text-gray-900 bg-gray-50 border-gray-200"
            raw
          />
        </div>
        {failedCategoryCount > 0 && (
          <p className="mt-3 text-xs text-amber-700">
            {failedCategoryCount} categor
            {failedCategoryCount !== 1 ? 'ies' : 'y'} did not complete and{' '}
            {failedCategoryCount !== 1 ? 'were' : 'was'} weighted at zero.
          </p>
        )}
      </div>

      {/* Run metadata */}
      <div className="bg-white rounded-lg shadow-sm border p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Run details</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          <div>
            <div className="text-xs text-gray-500 uppercase">Steps taken</div>
            <div className="text-lg font-semibold text-gray-900">
              {result.steps_taken}
            </div>
          </div>
          <div>
            <div className="text-xs text-gray-500 uppercase">Time elapsed</div>
            <div className="text-lg font-semibold text-gray-900">
              {result.time_seconds.toFixed(0)}s
            </div>
          </div>
          <div>
            <div className="text-xs text-gray-500 uppercase">Model</div>
            <div className="text-sm font-mono text-gray-900 truncate">
              {result.analysis_parameters?.model || 'Unknown'}
            </div>
          </div>
          <div>
            <div className="text-xs text-gray-500 uppercase">Max steps</div>
            <div className="text-lg font-semibold text-gray-900">
              {result.analysis_parameters?.max_steps ?? '—'}
            </div>
          </div>
        </div>
      </div>

      {/* Per-category review */}
      <div className="bg-white rounded-lg shadow-sm border p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Per-category review</h3>
        <div className="space-y-3">
          {result.categories.map((c) => (
            <CategoryCard key={c.category} category={c} />
          ))}
        </div>
      </div>

      {/* Recommendations */}
      {result.recommendations.length > 0 && (
        <div className="bg-white rounded-lg shadow-sm border p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Top recommendations</h3>
          <ol className="list-decimal pl-5 space-y-3">
            {result.recommendations.map((r, idx) => (
              <li key={idx} className="text-sm">
                <strong className="text-gray-900">{r.title}</strong>
                <div className="text-gray-700 mt-1">{r.detail}</div>
                {r.related_categories.length > 0 && (
                  <div className="text-xs text-gray-500 mt-1">
                    Surfaced by:{' '}
                    {r.related_categories
                      .map((c) => CATEGORY_DISPLAY_NAMES[c] ?? c)
                      .join(', ')}
                  </div>
                )}
              </li>
            ))}
          </ol>
        </div>
      )}

      {/* Methods statement */}
      <div className="bg-white rounded-lg shadow-sm border p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Methods statement</h3>
        <p className="text-xs text-gray-600 mb-2">
          Paste this into your Methods section. The full HTML report has dedicated
          copy buttons for the methods statement and the citation.
        </p>
        <textarea
          readOnly
          value={result.methods_statement}
          className="w-full text-xs font-mono p-3 bg-gray-50 border border-gray-300 rounded resize-y"
          rows={4}
        />
      </div>

      {/* Narrative */}
      {result.narrative_summary && (
        <div className="bg-white rounded-lg shadow-sm border p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-3">Narrative summary</h3>
          <pre className="whitespace-pre-wrap font-sans text-sm text-gray-700 leading-relaxed">
            {result.narrative_summary}
          </pre>
        </div>
      )}
    </div>
  );
};

interface ScoreTileProps {
  label: string;
  value: number;
  band: string;
  suffix?: string;
  raw?: boolean;
  note?: string;
}

const ScoreTile: React.FC<ScoreTileProps> = ({ label, value, band, suffix, raw, note }) => (
  <div className={`p-4 border rounded-lg ${band}`}>
    <div className="text-xs font-medium uppercase opacity-80">{label}</div>
    <div className="text-3xl font-bold mt-1">
      {Math.round(value)}
      {suffix && !raw && <span className="text-base font-medium opacity-70">{suffix}</span>}
    </div>
    {note && <div className="text-[10px] mt-0.5 opacity-70">{note}</div>}
  </div>
);

interface CategoryCardProps {
  category: import('../types/survey').CategoryReview;
}

const CategoryCard: React.FC<CategoryCardProps> = ({ category: c }) => {
  const display = CATEGORY_DISPLAY_NAMES[c.category as Category] ?? c.category;
  return (
    <div className={`border rounded-lg p-4 ${bandClass(c.score)}`}>
      <div className="flex items-center justify-between">
        <h4 className="font-semibold">{display}</h4>
        <div className="text-sm font-mono px-2 py-0.5 bg-white/70 rounded border">
          {Math.round(c.score)} / 100
        </div>
      </div>
      {c.error ? (
        <p className="text-sm mt-2 italic opacity-80">Reviewer error: {c.error}</p>
      ) : (
        <>
          <p className="text-sm mt-2 opacity-90">{c.summary}</p>
          {c.findings.length > 0 && (
            <details className="mt-2">
              <summary className="text-xs cursor-pointer opacity-80 hover:opacity-100">
                {c.findings.length} finding{c.findings.length !== 1 ? 's' : ''}
              </summary>
              <ul className="mt-2 space-y-2 text-sm">
                {c.findings.map((f, idx) => (
                  <li key={idx} className="bg-white/60 p-2 rounded border text-gray-800">
                    {f.locator && (
                      <span className="text-xs font-mono text-gray-500 mr-2">
                        {f.locator}
                      </span>
                    )}
                    <span className="text-xs uppercase font-semibold mr-2 text-gray-600">
                      {(['', 'low', 'med', 'high'][f.severity] ?? '?').toString()}
                    </span>
                    <div className="mt-1">{f.rationale}</div>
                    {f.excerpt && (
                      <blockquote className="mt-1 pl-2 border-l-2 border-gray-300 italic text-gray-600">
                        "{f.excerpt}"
                      </blockquote>
                    )}
                    {f.suggested_fix && (
                      <div className="mt-1 text-xs text-gray-700">
                        → {f.suggested_fix}
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            </details>
          )}
        </>
      )}
    </div>
  );
};

export default ResultsDashboard;
